/* DEV-291 v12 (2026-09-26): endless horizontal shelves on the home page (owner: "swipe right infinitely, no 'See all' at the end").
   Each .d291-sh[data-d291-shelf] row fetches more cards of its collection while the visitor swipes towards the end:
   /<locale>/collections/<handle>?section_id=dev291-shelf-page&page=N (Section Rendering API, sections/dev291-shelf-page.liquid,
   12 products a page, sold out ones left out there, the same product-card render as the shelf). Cards already in the row are
   skipped (page 1 overlaps the first cards), the row stops at the end of the collection. Without JS the "See all" card stays.
   Cards need no extra set-up: lm-card.js binds every product-card added to the page.
   v13 (2026-09-27, DEV-394): later pages also skip products already in the other shelves or the feed below.
   2026-09-30 (Audrius: on a computer the shelves did not move when dragged sideways with the mouse): with a mouse the
   shelf rows (and the "Pagal progą" rows) are dragged sideways both ways, a click after a drag does not open the product,
   and each shelf gets previous/next arrows next to its title. Touch and trackpad keep the native scroll. */
(() => {
  const started = new WeakSet();

  const init = (root) => {
    if (!root || started.has(root)) return;
    const row = root.querySelector('.d291-sh__row');
    const url = root.dataset.url;
    if (!row || !url || !window.fetch || !window.DOMParser) return;
    started.add(root);
    root.classList.add('d291-sh--js');
    const fallback = row.querySelector('.d291-sh__last');
    if (fallback) fallback.remove();

    let next = 1;
    let pages = Infinity;
    let loading = false;
    let stopped = false;
    let frame = 0;
    const loader = document.createElement('li');
    loader.className = 'd291-sh__load';
    loader.setAttribute('aria-hidden', 'true');
    loader.innerHTML = '<i></i><i></i><i></i>';

    /* v13 (DEV-394): skip every product already on the page (this row, the other shelves and the "You may also like" feed), so one product shows once */
    const handles = () => new Set(Array.from(document.querySelectorAll('[data-d291-shelf] product-card[handle], .d291-fy__grid product-card[handle]')).map((card) => card.getAttribute('handle')));

    const finish = () => {
      stopped = true;
      loader.remove();
      row.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      root.classList.add('d291-sh--end');
      row.removeAttribute('aria-busy');
    };

    const pageItems = async (n) => {
      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', 'dev291-shelf-page');
      u.searchParams.set('page', n);
      const response = await fetch(u.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = doc.querySelector('[data-d291-shelf-page]');
      if (!list) throw new Error('no list');
      pages = parseInt(list.dataset.pages, 10) || 0;
      if ((parseInt(list.dataset.page, 10) || 0) !== n) return []; /* past the last page */
      return Array.from(list.children);
    };

    const append = async () => {
      if (loading || stopped) return;
      if (next > pages) { finish(); return; }
      loading = true;
      row.setAttribute('aria-busy', 'true');
      row.append(loader);
      try {
        const items = await pageItems(next);
        next += 1;
        const seen = handles();
        const nodes = [];
        items.forEach((item) => {
          const card = item.querySelector('product-card[handle]');
          if (!card || seen.has(card.getAttribute('handle'))) return;
          seen.add(card.getAttribute('handle'));
          const node = document.importNode(item, true);
          node.querySelectorAll('[reveal-js]').forEach((el) => el.removeAttribute('reveal-js'));
          node.querySelectorAll('script').forEach((el) => el.remove());
          nodes.push(node);
        });
        loader.before(...nodes);
        if (next > pages) finish();
      } catch (error) {
        finish();
      } finally {
        loading = false;
        if (!stopped) row.removeAttribute('aria-busy');
      }
      if (!stopped) check();
    };

    /* load when less than two screens of cards are left to the right */
    function check() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (stopped || loading) return;
        const left = row.scrollWidth - row.scrollLeft - row.clientWidth;
        if (left < row.clientWidth * 2) append();
      });
    }

    row.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    if ('IntersectionObserver' in window) {
      /* start once the shelf is near the viewport, so page 1 is ready before the first swipe */
      const io = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) { io.disconnect(); check(); }
      }, { rootMargin: '300px 0px' });
      io.observe(root);
    } else {
      check();
    }
  };

  /* ---------- mouse: drag and arrows (2026-09-30) ---------- */
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const NAV = {
    en: ['Previous', 'Next'], lt: ['Ankstesni', 'Kiti'], de: ['Zurück', 'Weiter'], da: ['Forrige', 'Næste'], fi: ['Edellinen', 'Seuraava'],
    fr: ['Précédent', 'Suivant'], nl: ['Vorige', 'Volgende'], no: ['Forrige', 'Neste'], sv: ['Föregående', 'Nästa']
  };
  const lang = (document.documentElement.lang || 'en').toLowerCase().slice(0, 2);
  const navText = NAV[lang === 'nb' ? 'no' : lang] || NAV.en;

  const addStyle = () => {
    if (document.getElementById('d291-sh-mouse')) return;
    const style = document.createElement('style');
    style.id = 'd291-sh-mouse';
    style.textContent =
      '.d291-drag-ok{cursor:grab}' +
      '.d291-drag-on{cursor:grabbing;scroll-snap-type:none!important;scroll-behavior:auto!important;user-select:none}' +
      '.d291-drag-on a,.d291-drag-on img{pointer-events:none}' +
      '.d291-sh--nav .d291-sh__head{margin-right:auto}' +
      '.d291-sh__nav{display:flex;gap:8px;flex:none;align-self:center}' +
      '.d291-sh__nav button{display:flex;align-items:center;justify-content:center;width:36px;height:36px;padding:0;border:1px solid #1a1a1a;border-radius:50%;background:#fff;color:#1a1a1a;cursor:pointer}' +
      '.d291-sh__nav button:disabled{opacity:.3;cursor:default}' +
      '.d291-sh__nav svg{width:14px;height:14px}';
    document.head.append(style);
  };

  const dragRow = (row) => {
    if (!row || row.__d291Drag) return;
    row.__d291Drag = true;
    row.classList.add('d291-drag-ok');
    let startX = 0;
    let startLeft = 0;
    let down = false;
    let moved = false;
    const move = (event) => {
      if (!down) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) < 6) return;
      if (!moved) {
        moved = true;
        row.classList.add('d291-drag-on');
      }
      row.scrollLeft = startLeft - dx;
      event.preventDefault();
    };
    const up = () => {
      if (!down) return;
      down = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (!moved) return;
      /* back to scroll-snap: the row settles on the nearest card; the click that ends the drag is dropped below */
      const left = row.scrollLeft;
      row.classList.remove('d291-drag-on');
      row.scrollLeft = left;
      setTimeout(() => { moved = false; }, 0);
    };
    row.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || row.scrollWidth <= row.clientWidth + 1) return;
      if (event.target.closest('button, input, label, [data-lm-heart], [data-lm-qs-size], [data-lm-qadd], .lm-dots')) return;
      down = true;
      moved = false;
      startX = event.clientX;
      startLeft = row.scrollLeft;
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    });
    /* links and photos are not dragged out of the row as ghosts */
    row.addEventListener('dragstart', (event) => event.preventDefault());
    row.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);
  };

  const arrow = (dir) => '<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="' + (dir < 0 ? 'M9 1.5 3.5 7 9 12.5' : 'M5 1.5 10.5 7 5 12.5') + '" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';

  const addArrows = (root) => {
    const row = root.querySelector('.d291-sh__row');
    const top = root.querySelector('.d291-sh__top');
    if (!row || !top || top.querySelector('.d291-sh__nav')) return;
    const nav = document.createElement('div');
    nav.className = 'd291-sh__nav';
    nav.innerHTML = [-1, 1].map((dir) => '<button type="button" data-dir="' + dir + '" aria-label="' + navText[dir < 0 ? 0 : 1] + '">' + arrow(dir) + '</button>').join('');
    top.append(nav);
    root.classList.add('d291-sh--nav');
    const [prev, next] = nav.querySelectorAll('button');
    const sync = () => {
      prev.disabled = row.scrollLeft <= 2;
      next.disabled = row.scrollWidth - row.scrollLeft - row.clientWidth <= 2 && root.classList.contains('d291-sh--end');
    };
    nav.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-dir]');
      if (!button || button.disabled) return;
      /* one screen less one card, so the last card in view stays as the first one */
      const card = row.querySelector(':scope > li');
      const step = Math.max(row.clientWidth - (card ? card.getBoundingClientRect().width : 0), row.clientWidth * 0.5);
      row.scrollBy({ left: step * Number(button.dataset.dir), behavior: 'smooth' });
    });
    let frame = 0;
    row.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    }, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    new MutationObserver(sync).observe(root, { attributes: true, attributeFilter: ['class'] });
    sync();
  };

  const mouse = () => {
    if (!fine.matches) return;
    addStyle();
    document.querySelectorAll('[data-d291-shelf]').forEach((root) => {
      dragRow(root.querySelector('.d291-sh__row'));
      addArrows(root);
    });
    document.querySelectorAll('.d291-oc__rows').forEach(dragRow);
  };

  const boot = () => {
    document.querySelectorAll('[data-d291-shelf]').forEach(init);
    mouse();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
